"use client";
import { useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode } from "swiper/modules";
import { Swiper as SwiperType } from "swiper";
import FallbackImage from "@/app/components/elements/FallbackImage";
import StarRating from "../../elements/StarRating";
import SwiperButton from "../../elements/buttons/SwiperButton";
import useModal from "@/app/lib/features/modal/modal.hook";
import { StoreReview } from "@/app/lib/features/store/store.types";
import { trackGeneric } from "@/app/lib/tracking/interactions";

interface StoreReviewsSectionProps {
  title?: string;
  reviews: StoreReview[];
}

const StoreReviewsSection = ({
  title = "Görselli Yorumlar",
  reviews,
}: StoreReviewsSectionProps) => {
  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(true);
  const { createModal } = useModal();

  if (!reviews.length) return null;

  const updateGradients = (swiper: SwiperType) => {
    setShowLeftGradient(!swiper.isBeginning);
    setShowRightGradient(!swiper.isEnd);
  };

  const handleReviewClick = (review: StoreReview) => {
    trackGeneric("store_review_open", "click");
    createModal("review-detail", { review });
  };

  return (
    <section>
      <h2 className="text-lg sm:text-xl font-medium mb-4">{title}</h2>
      <Swiper
        className="relative"
        spaceBetween={16}
        slidesPerView={1.5}
        freeMode={true}
        modules={[FreeMode]}
        onSwiper={updateGradients}
        onSlideChange={updateGradients}
        breakpoints={{
          480: { slidesPerView: 2, spaceBetween: 16 },
          640: { slidesPerView: 2.5, spaceBetween: 16 },
          768: { slidesPerView: 3, spaceBetween: 16 },
          1024: { slidesPerView: 3.5, spaceBetween: 20 },
          1280: { slidesPerView: 4, spaceBetween: 20 },
        }}
      >
        {reviews.map((review) => (
          <SwiperSlide key={review.id}>
            <button
              onClick={() => handleReviewClick(review)}
              className="group cursor-pointer bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow w-full text-left"
            >
              <div className="relative w-full h-72 overflow-hidden">
                <FallbackImage
                  src={review.images[0]}
                  alt={review.customer_name}
                  fill
                  sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
                  className="object-cover group-hover:scale-[1.02] transition-transform duration-200 ease-out"
                />
              </div>
              <div className="p-4 space-y-3">
                <StarRating rating={review.rating} size="sm" />
                <p className="text-sm text-gray-700 line-clamp-3">
                  {review.comment}
                </p>
                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <span className="text-sm font-medium text-gray-900 truncate">
                    {review.customer_name}
                  </span>
                  <span className="text-xs text-gray-500 whitespace-nowrap ml-2">
                    {review.date}
                  </span>
                </div>
              </div>
            </button>
          </SwiperSlide>
        ))}
        <div className="absolute z-50 top-1/2 -translate-y-1/2 left-0 w-full items-center justify-between px-3 pointer-events-none hidden xl:flex">
          <SwiperButton position="left" slideMode="group" />
          <SwiperButton position="right" slideMode="group" />
        </div>
        <div
          className={`absolute top-0 left-0 z-20 w-5 sm:w-8 lg:w-13.75 h-full bg-linear-to-r from-white to-transparent transition-opacity duration-300 pointer-events-none hidden xl:block ${showLeftGradient ? "opacity-100" : "opacity-0"}`}
        />
        <div
          className={`absolute top-0 right-0 z-20 w-5 sm:w-8 lg:w-13.75 h-full bg-linear-to-l from-white to-transparent transition-opacity duration-300 pointer-events-none hidden xl:block ${showRightGradient ? "opacity-100" : "opacity-0"}`}
        />
      </Swiper>
    </section>
  );
};

export default StoreReviewsSection;
